import type { Bundle, BundleEntry, FhirResource } from 'fhir/r4';

export const TRANSACTION_BUNDLE_TYPE = 'transaction';
export const SDOH_CATEGORY_REFERRAL = 'referral';
export const SDOH_CATEGORY_FOOD_INSECURITY = 'food-insecurity';
export const SDOH_CATEGORY_TRANSPORTATION_INSECURITY = 'transportation-insecurity';

export function buildEmptyBundle(type: Bundle['type']): Bundle {
  return {
    resourceType: 'Bundle',
    type: type,
  };
}

export function buildBundle(type: Bundle['type'], bundleEntries: BundleEntry[]): Bundle {
  return {
    resourceType: 'Bundle',
    type: type,
    entry: bundleEntries,
  };
}

export function buildResourceEntries(resources: FhirResource[], deleteFlag: boolean): BundleEntry[] {
  return resources.map((resource) => {
    return buildResourceEntry(resource, deleteFlag);
  });
}

export function buildResourceEntry(resource: FhirResource, deleteFlag: boolean): BundleEntry {
  return {
    fullUrl: resource.resourceType + '/' + resource.id,
    resource: resource,
    request: {
      method: deleteFlag ? 'DELETE' : 'PUT',
      url: resource.resourceType + '/' + resource.id,
    },
  };
}

/**
 * Stamps `identifier[last-modified-by]` onto a resource and returns the SAME object.
 *
 * Generic over the resource so callers keep the type they passed in: every call site
 * writes the result back over its own variable (`task = addUpdatedBy(task, …)`), and
 * an `any` return erased the resource type at each of those assignments.
 */
export function addUpdatedBy<T extends object>(resource: T, updatedBy: string): T {
  if (updatedBy) {
    const updatedIdentifers: { system?: string; value?: string }[] = [];
    // Reached structurally rather than through `T`: callers pass several different
    // resource types, and some (`observation.ts`) build a literal with no
    // `identifier` at all — this stamps one on.
    const held = resource as { identifier?: { system?: string; value?: string }[] };
    const identifiers = held.identifier ? held.identifier : [];
    if (identifiers.length === 0) {
      updatedIdentifers.push({
        system: 'https://projectwell.io/fhir/identifiers/last-modified-by',
        value: updatedBy,
      });
    } else {
      let updatedByIdentifierExists = false;
      for (let i = 0; i < identifiers.length; i++) {
        // `i` is bounded by `identifiers.length`, so the element is present;
        // `noUncheckedIndexedAccess` cannot see that.
        const identifier = identifiers[i]!;
        if (identifier.system === 'https://projectwell.io/fhir/identifiers/last-modified-by') {
          identifier.value = updatedBy;
          updatedByIdentifierExists = true;
        }
        updatedIdentifers.push(identifier);
      }
      if (!updatedByIdentifierExists) {
        updatedIdentifers.push({
          system: 'https://projectwell.io/fhir/identifiers/last-modified-by',
          value: updatedBy,
        });
      }
    }
    held.identifier = updatedIdentifers;
  }

  return resource;
}
